import MiniEvent from "./mini-event";
import { ValidationError } from "./error-utils.js";

const uploadEvents = new MiniEvent();
const uploadsByKey = new Map();

/**
 * Handles file upload to a specified category on the server with progress tracking.
 * Validates the file before initiating the upload process.
 *
 * @param {Object} params - Parameters required for file upload.
 * @param {File} params.file - The file to be uploaded.
 * @param {string} params.category - The category where the file is to be uploaded.
 * @param {Function} params.onProgress - Callback function to track upload progress.
 * @param {string} params.key - The key used for file validation.
 * @param {string} params.accept - List of accepted file types for validation.
 * @returns {Promise<Object>} A promise that resolves with the server response and the file object upon successful upload, or rejects with an error message if an upload error occurs.
 */
const postFile = ({ file, category, onProgress, key, accept }) => {
  validateFile(file, key, accept);
  return new Promise((resolve, reject) => {
    const data = new FormData();
    data.append("file", file);

    const request = new XMLHttpRequest();
    request.open("POST", `${import.meta.env.VITE_API_HOST}/upload/${category}`);
    request.withCredentials = true;

    // Store references to handlers for cleanup
    const progressHandler = function (e) {
      const completed = Math.floor((e.loaded / e.total) * 100);
      onProgress(completed);
    };

    const loadHandler = function () {
      cleanup(); // Clean up listeners before resolving
      if (request.status < 400) {
        resolve({ ...JSON.parse(request.response), file });
      } else {
        console.error(
          `Failed Upload with ${request.status}: ${request.response}`,
        );
        reject(JSON.parse(request.response));
      }
    };

    const errorHandler = function (e) {
      cleanup(); // Clean up listeners before rejecting
      reject(e);
    };

    const abortHandler = function () {
      cleanup(); // Clean up listeners if aborted
      reject(new Error("Upload aborted"));
    };

    // Function to clean up all listeners
    const cleanup = () => {
      request.upload.removeEventListener("progress", progressHandler);
      request.removeEventListener("load", loadHandler);
      request.removeEventListener("error", errorHandler);
      request.removeEventListener("abort", abortHandler);
    };

    // Add event listeners
    request.upload.addEventListener("progress", progressHandler);
    request.addEventListener("load", loadHandler);
    request.addEventListener("error", errorHandler);
    request.addEventListener("abort", abortHandler);

    request.send(data);
  });
};

/**
 * Asynchronously handles the uploading of a file while providing progress updates
 * and managing upload state for the specified key.
 *
 * @param {Object} options - The options for the file upload.
 * @param {string} options.key - The unique key representing the upload. Used to manage file upload states.
 * @param {string} options.category - The category or type of the file being uploaded.
 * @param {File} options.file - The file object to be uploaded.
 * @param {function} [options.onUploaded] - A callback function invoked after a successful upload with the result as an argument.
 * @param {function} [options.onError] - A callback function invoked when an error occurs during the upload.
 * @param {string} [options.accept] - A string specifying accepted file types for the upload.
 *
 * @returns {Promise<void>} A promise that resolves when the upload has completed, or rejects if an error occurs.
 */
export const uploadFile = async ({
  key,
  category,
  file,
  onUploaded,
  onError,
  accept,
}) => {
  const info = { progress: 0, file };

  const fileList = uploadsByKey.get(key) || [];
  fileList.push(info);
  uploadsByKey.set(key, fileList);
  uploadEvents.emit(key, fileList);

  const update = (props) => {
    Object.assign(info, props);
    uploadEvents.emit(key, fileList);
  };

  let result;
  try {
    result = await postFile({
      file,
      category,
      onProgress: (progress) => update({ progress }),
      key,
      accept,
    });
  } catch (e) {
    if (onError) {
      onError(e);
    } else {
      return Promise.reject(e);
    }
  }

  if (result) onUploaded(result);

  fileList.splice(fileList.indexOf(info), 1);
  if (fileList.length === 0) uploadsByKey.delete(key);
  uploadEvents.emit(key, fileList);
};

/**
 * Generates a regular expression that matches any of the media types specified in the provided accept string.
 *
 * @param {string} accept - A comma-separated list of media types.
 * @returns {RegExp} A regular expression that matches any media type in the accept list.
 */
const createAcceptRegex = (accept) => {
  const acceptPatterns = accept.split(",").map((type) => {
    const regexType = type.trim().replace("*", ".*");
    return `(${regexType})`;
  });
  return new RegExp(`^(${acceptPatterns.join("|")})$`);
};

/**
 * Validates a file based on its type and size.
 *
 * @param {File} file - The file object to be validated.
 * @param {string} fieldName - The name of the form field associated with the file.
 * @param {string|null} accept - A comma-separated string of acceptable MIME types. If null, all file types are accepted.
 *
 * @throws {ValidationError} Throws an error if the file type is not among the accepted types, or if the file size exceeds the maximum limit.
 */
const validateFile = (file, fieldName, accept) => {
  // If accept is null, any file type is accepted. However, if it is for instance
  // "image/*,audio/*,application/*,text/*" we need to validate the input.
  if (accept) {
    const acceptRegex = createAcceptRegex(accept);
    if (!acceptRegex.test(file.type)) {
      throw new ValidationError({
        message: `Dieses Dateiformat kann nicht gelesen werden. Bitte uploade nur folgende Dateitypen: ${accept}`,
        field: fieldName,
      });
    }
  }

  const maxSize = 25 * 1024 * 1024; // 25MB
  if (file.size > maxSize) {
    throw new ValidationError({
      message:
        "Dieses Video ist zu groß. Bitte editiere dein Video, sodass es maximal 25MB groß ist.",
      field: fieldName,
    });
  }
};
